import React, { useEffect, useState, useMemo, useRef } from "react";
import {
    Box, Typography, Modal, IconButton, TextField,
    Button, Link, Divider, useTheme,
    Grid, CircularProgress,
    List, ListItem, ListItemText, Avatar,
    InputAdornment, Chip,
    Snackbar, Alert, FormControl, InputLabel,
    MenuItem, Select
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import StarIcon from "@mui/icons-material/Star";
import CakeIcon from "@mui/icons-material/Cake";
import SendIcon from "@mui/icons-material/Send";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { format, differenceInYears } from "date-fns";
import { ActivityBookingDetails, Passenger } from "../model/ActivityBookingDetails";
import { BookingNote } from "../model/BookingNote";
import { useAuth } from "../../auth/AuthContext/AuthContext";
import { upsertBookingNote } from "../routes/BookingNotesRoute";
import { uploadTicketPdf } from "../routes/TicketRoute";
import { syncBooking } from "../routes/syncBooking";
import { shouldSyncBokunBooking } from "../model/booking-flow-utils";
//import { StatusBadge } from "./StatusBadge";
import { useBookingToast } from "../model/useBookingToast";
import { PROCESS_ERROR_STATUSES, SendBookingStatus, TERMINAL_PROCESS_STATUSES, normalizeProcessStatus } from "../../../api/socketStatusModel";
/* ===================== UTILS ===================== */

const calculateAge = (dob: string | null) => {
    if (!dob) return null;
    const age = differenceInYears(new Date(), new Date(dob));
    return isNaN(age) ? null : `${age} anni`;
};

const safeFormatDate = (value: string | Date | null | undefined, pattern: string) => {
    if (!value) return "-";

    const parsedDate = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return "-";

    return format(parsedDate, pattern);
};

const getTicketSummaryLines = (value: unknown) => {
    if (typeof value !== "string" || !value.trim()) return [];

    return value.split(', ').map((ticket) => {
        const lastSpace = ticket.lastIndexOf(' ');

        if (lastSpace <= 0) {
            return { label: ticket, quantity: "" };
        }

        return {
            label: ticket.substring(0, lastSpace),
            quantity: ticket.substring(lastSpace + 1)
        };
    });
};

type RowProps = {
    label: string;
    value?: any;
    labelSx?: any;
};

const Row = ({ label, value, labelSx }: RowProps) => (
    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 0.2, sm: 0 }, mb: 0.8 }}>
        <Typography
            sx={{
                width: { xs: "100%", sm: 160 },
                fontSize: "0.9rem",
                fontWeight: 500,
                color: "text.secondary",
                flexShrink: 0,
                ...labelSx
            }}
        >
            {label}
        </Typography>
        <Box sx={{ flex: 1, fontSize: "0.9rem", color: "text.primary", wordBreak: "break-word" }}>
            {value ?? "-"}
        </Box>
    </Box>
);

/* ===================== PROPS ===================== */

interface Props {
    open: boolean;
    booking: ActivityBookingDetails | null;
    notes: BookingNote[];
    ticketNumber: string;
    ticketSent: boolean;
    pdfLink: string;
    onPdfLinkChange: (v: string) => void;
    audioGuideLink: string;
    onAudioGuideLinkChange: (v: string) => void;
    isSavingNotes?: boolean;
    isSavingTicket?: boolean;
    processStatus?: { status: string; message?: string; error?: string };
    onAddNote: (text: any) => Promise<void>;
    onTicketNumberChange: (v: string) => void;
    onTicketSentChange: (v: boolean) => void;
    onSaveTicket: (payload: SaveTicketPayload) => Promise<void>;
    onClose: () => void;
    webhookStatus?: string;
    lastError?: string;
}

export interface SaveTicketPayload {
    ticketNumber: string;
    pdfLink: string;
    audioGuideLink: string;
    ticketLanguage: TicketLanguage;
    ticketSent: boolean;
}

export const ticketLanguages = {
    en: "INGLESE",
    it: "ITALIANO",
    de: "TEDESCO",
    es: "SPAGNOLO",
    fr: "FRANCESE",
    kr: "COREANO",
    cn: "CINESE",
    jp: "GIAPPONESE",
} as const;

export type TicketLanguage = keyof typeof ticketLanguages;
type TicketLanguageValue = TicketLanguage | "";

const UFFIZI_AUDIO_GUIDE_URL = "https://api.slowtourtuscany.it/audioguide/Uffizi_Audioguide.pdf";

const ticketLanguageEntries = Object.entries(ticketLanguages) as Array<[TicketLanguage, (typeof ticketLanguages)[TicketLanguage]]>;

const getTicketLanguage = (value?: string | null): TicketLanguageValue => {
    const normalizedValue = value?.toLowerCase() as TicketLanguage | undefined;
    return normalizedValue && normalizedValue in ticketLanguages ? normalizedValue : "";
};

/* ===================== COMPONENT ===================== */

const BookingDetailModal: React.FC<Props> = ({
    open,
    booking,
    notes,
    ticketNumber,
    ticketSent,
    pdfLink,
    audioGuideLink,
    isSavingNotes = false,
    isSavingTicket = false,
    onAddNote,
    onTicketNumberChange,
    onTicketSentChange,
    onPdfLinkChange,
    onAudioGuideLinkChange,
    processStatus,
    onSaveTicket,
    onClose,
    webhookStatus,
    lastError,
}) => {
    const theme = useTheme();
    const { userId } = useAuth();
    const currentBookingId = booking?.BookingId;
    const currentExternalActivityId = booking?.ExternalActivityId;
    const currentTicketLanguage = booking?.ticketLanguage;
    const usesUffiziAudioGuide = ["tk2", "tk3"].includes(String(booking?.externalId ?? "").trim().toLowerCase());

    // STATI LOCALI PER INPUT FLUIDI
    const [noteInput, setNoteInput] = useState("");
    const [localSavingNote, setLocalSavingNote] = useState(false);
    const [pdfDownloadLink, setPdfDownloadLink] = useState(pdfLink);
    const [audioGuideDownloadLink, setAudioGuideDownloadLink] = useState(audioGuideLink);
    const [ticketLanguage, setTicketLanguage] = useState<TicketLanguageValue>("");
    const [showProcessLoading, setShowProcessLoading] = useState(false);
    const [showProcessFeedback, setShowProcessFeedback] = useState(false);
    const feedbackResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Sincronizzazione locale quando il PDF cambia nel padre
    //useEffect(() => {
    //    if (open) setPdfDownloadLink(pdfLink);
    //}, [pdfLink, open]);

    const [localTicketNumber, setLocalTicketNumber] = useState(ticketNumber);

    // MODIFICA 2026-08-19: Stato per sincronizzazione automatica da Bokun
    const [syncing, setSyncing] = useState(false);
    const syncSessionRef = useRef<{ bookingId: number | undefined; confirmationCodes: Set<string> }>({
        bookingId: undefined,
        confirmationCodes: new Set<string>(),
    });

    // Upload PDF
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [isUploadingPdf, setIsUploadingPdf] = useState(false);
    const [pendingPdfFile, setPendingPdfFile] = useState<File | null>(null);

    // Upload Audio PDF
    const audioFileInputRef = useRef<HTMLInputElement | null>(null);
    const [isUploadingAudioPdf, setIsUploadingAudioPdf] = useState(false);
    const [pendingAudioPdfFile, setPendingAudioPdfFile] = useState<File | null>(null);

    // Stato Snackbar
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" | "info" }>({
        open: false, message: "", severity: "success"
    });

    // Sincronizza i campi locali quando il padre aggiorna i dati della prenotazione.
    // Non resettiamo qui il feedback del processo, altrimenti il badge sparisce
    // subito dopo il salvataggio quando il padre aggiorna le props.
    useEffect(() => {
        if (!open || !currentBookingId) return;

        setPdfDownloadLink(pdfLink || "");
        setAudioGuideDownloadLink(usesUffiziAudioGuide ? UFFIZI_AUDIO_GUIDE_URL : (audioGuideLink || ""));
        const nextLanguage = getTicketLanguage(currentTicketLanguage);
        setTicketLanguage(usesUffiziAudioGuide && ["kr", "cn", "jp"].includes(nextLanguage) ? "" : nextLanguage);
        setLocalTicketNumber(ticketNumber || "");
        setPendingPdfFile(null);
        setPendingAudioPdfFile(null);
    }, [currentBookingId, currentTicketLanguage, open, ticketNumber, pdfLink, audioGuideLink, usesUffiziAudioGuide]);

    // Il feedback del processo va resettato solo quando si apre una nuova prenotazione
    // o si chiude la modale, non durante gli update intermedi dei campi.
    useEffect(() => {
        if (!open) {
            setShowProcessLoading(false);
            setShowProcessFeedback(false);
            return;
        }

        if (currentBookingId) {
            setShowProcessLoading(false);
            setShowProcessFeedback(false);
        }
    }, [currentBookingId, open]);

    const syncExternalId = booking?.externalId?.trim().toUpperCase() ?? "";
    const syncChannelType = booking?.ChannelType?.trim().toUpperCase() ?? "";
    const syncConfirmationCode = booking?.ConfirmationCode?.trim() ?? "";

    // Synchronization is attempted once per confirmation code during each modal session.
    useEffect(() => {
        if (!open) {
            syncSessionRef.current = { bookingId: undefined, confirmationCodes: new Set<string>() };
            setSyncing(false);
            return;
        }

        if (syncSessionRef.current.bookingId !== currentBookingId) {
            syncSessionRef.current = { bookingId: currentBookingId, confirmationCodes: new Set<string>() };
        }

        if (!shouldSyncBokunBooking(syncExternalId, syncChannelType, syncConfirmationCode)) return;
        if (syncSessionRef.current.confirmationCodes.has(syncConfirmationCode)) return;

        syncSessionRef.current.confirmationCodes.add(syncConfirmationCode);
        let isActive = true;
        const synchronize = async (): Promise<void> => {
            setSyncing(true);
            setSnackbar({ open: true, message: "Aggiornamento dati da Bokun in corso...", severity: "info" });
            try {
                const result = await syncBooking(syncConfirmationCode);
                if (!result.success) throw new Error("Bokun non ha confermato la sincronizzazione.");
                if (isActive) {
                    setSnackbar({ open: true, message: "Dati Bokun sincronizzati. Aggiornamento prenotazione in corso.", severity: "success" });
                }
            } catch (error) {
                if (isActive) {
                    const message = error instanceof Error ? error.message : "Errore sconosciuto";
                    setSnackbar({ open: true, message: `Sincronizzazione Bokun non riuscita: ${message}`, severity: "error" });
                }
            } finally {
                if (isActive) setSyncing(false);
            }
        };

        void synchronize();
        return () => {
            isActive = false;
        };
    }, [open, currentBookingId, syncExternalId, syncChannelType, syncConfirmationCode]);

    useBookingToast(webhookStatus, booking?.externalId, lastError);

    const passengers = useMemo<Passenger[]>(() => {
        if (!booking?.TicketSummaryDetails) return [];
        try {
            const parsed = JSON.parse(booking.TicketSummaryDetails);
            return parsed.sort((a: Passenger, b: Passenger) =>
                a.TicketCategory.localeCompare(b.TicketCategory)
            );
        } catch (e) {
            return [];
        }
    }, [booking?.TicketSummaryDetails]);
    const normalizedProcessStatus = normalizeProcessStatus(processStatus?.status);
    const isProcessError = Boolean(processStatus?.error) || PROCESS_ERROR_STATUSES.has(normalizedProcessStatus);
    const isProcessRunning = Boolean(processStatus) && !isProcessError && !TERMINAL_PROCESS_STATUSES.has(normalizedProcessStatus);
    const isActionDisabled = isSavingTicket || (showProcessLoading && isProcessRunning);

    useEffect(() => {
        if (feedbackResetTimerRef.current) {
            clearTimeout(feedbackResetTimerRef.current);
            feedbackResetTimerRef.current = null;
        }

        if (!processStatus) {
            setShowProcessLoading(false);
            return;
        }

        // Funzione per pulire la chiave processuale dopo il timeout
        const cleanupProcessStatus = () => {
            if (typeof window !== 'undefined' && window.dispatchEvent) {
                const event = new CustomEvent('cleanupProcessStatus', {
                    detail: { bookingId: currentExternalActivityId }
                });
                window.dispatchEvent(event);
            }
        };

        // Chiudi feedback quando arriva COMPLETED o EMAIL_SENT
        if (
            normalizedProcessStatus === SendBookingStatus.COMPLETED ||
            normalizedProcessStatus === SendBookingStatus.EMAIL_SENT
        ) {
            setShowProcessLoading(false);
            if (showProcessFeedback) {
                feedbackResetTimerRef.current = setTimeout(() => {
                    setShowProcessFeedback(false);
                    cleanupProcessStatus();
                }, 2000); // Timeout breve per UX fluida
            }
        } else if (TERMINAL_PROCESS_STATUSES.has(normalizedProcessStatus)) {
            setShowProcessLoading(false);
            if (showProcessFeedback) {
                feedbackResetTimerRef.current = setTimeout(() => {
                    setShowProcessFeedback(false);
                    cleanupProcessStatus();
                }, isProcessError ? 8000 : 5000);
            }
        }

        return () => {
            if (feedbackResetTimerRef.current) {
                clearTimeout(feedbackResetTimerRef.current);
                feedbackResetTimerRef.current = null;
            }
        };
    }, [processStatus, normalizedProcessStatus, isProcessError, showProcessFeedback, currentExternalActivityId]);

    if (!booking) return null;

    const customerName = [booking.FirstName, booking.LastName]
        .filter((name): name is string => Boolean(name?.trim()))
        .map((name) => name.trim())
        .join(" ");

    const showMessage = (message: string, severity: "success" | "error") => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

    const handleCloseModal = () => {
        setShowProcessLoading(false);
        setShowProcessFeedback(false);
        setSnackbar((prev) => ({ ...prev, open: false }));
        onClose();
    };

    const copyToClipboard = (text: string) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        showMessage("Copiato negli appunti!", "success");
    };

    // Azione: Salva e Invia
    const handleSendAndSave = async () => {
        const nextTicketNumber = localTicketNumber.trim();
        let nextPdfLink = pdfDownloadLink.trim();
        let nextAudioGuideLink = usesUffiziAudioGuide ? UFFIZI_AUDIO_GUIDE_URL : audioGuideDownloadLink.trim();
        if (!nextTicketNumber || !nextPdfLink) return;
        if (!ticketLanguage) {
            showMessage("Seleziona una lingua prima di salvare.", "error");
            return;
        }

        if (pendingPdfFile) {
            try {
                nextPdfLink = await uploadPendingPdf(booking?.ExternalActivityId);
            } catch (error) {
                showMessage(`Errore upload PDF: ${error instanceof Error ? error.message : "Errore sconosciuto"}`, "error");
                return;
            }
        }

        if (!usesUffiziAudioGuide && pendingAudioPdfFile) {
            try {
                nextAudioGuideLink = await uploadPendingAudioPdf(booking?.ExternalActivityId);
            } catch (error) {
                showMessage(`Errore upload PDF audioguida: ${error instanceof Error ? error.message : "Errore sconosciuto"}`, "error");
                return;
            }
        }

        // Sincronizziamo il padre prima di procedere
        setShowProcessLoading(true);
        setShowProcessFeedback(true);
        onTicketNumberChange(nextTicketNumber);
        onPdfLinkChange(nextPdfLink);
        onAudioGuideLinkChange(nextAudioGuideLink);

        try {
            await onSaveTicket({
                
                ticketNumber: nextTicketNumber,
                pdfLink: nextPdfLink,
                audioGuideLink: nextAudioGuideLink,
                ticketLanguage: ticketLanguage,
                ticketSent: true,
            });
            //  showMessage("Biglietto inviato e salvato con successo!", "success");
        } catch (error) {
            showMessage("Errore durante l'invio del biglietto.", "error");
        }
    };

    // Azione: Solo Salva
    const handleSaveTicketOnly = async () => {
        if (isSavingTicket) return;

        const nextTicketNumber = localTicketNumber.trim();
        let nextPdfLink = pdfDownloadLink.trim();
        let nextAudioGuideLink = usesUffiziAudioGuide ? UFFIZI_AUDIO_GUIDE_URL : audioGuideDownloadLink.trim();

        if (!nextTicketNumber) return;

        if (!ticketLanguage) {
            showMessage("Seleziona una lingua prima di salvare.", "error");
            return;
        }

        if (pendingPdfFile) {
            try {
                nextPdfLink = await uploadPendingPdf(booking?.ExternalActivityId);
            } catch (error) {
                showMessage(`Errore upload PDF: ${error instanceof Error ? error.message : "Errore sconosciuto"}`, "error");
                return;
            }
        }

        if (!usesUffiziAudioGuide && pendingAudioPdfFile) {
            try {
                nextAudioGuideLink = await uploadPendingAudioPdf(booking?.ExternalActivityId);
            } catch (error) {
                showMessage(`Errore upload PDF audioguida: ${error instanceof Error ? error.message : "Errore sconosciuto"}`, "error");
                return;
            }
        }

        // Sincronizziamo il padre
        setShowProcessLoading(true);
        setShowProcessFeedback(true);
        onTicketNumberChange(nextTicketNumber);
        onPdfLinkChange(nextPdfLink);
        onAudioGuideLinkChange(nextAudioGuideLink);

        try {
            await onSaveTicket({
                ticketNumber: nextTicketNumber,
                pdfLink: nextPdfLink,
                audioGuideLink: nextAudioGuideLink,
                ticketLanguage: ticketLanguage,
                ticketSent: false,
            });
            showMessage("Numero biglietto salvato!", "success");
        } catch (error) {
            showMessage("Errore nel salvataggio del biglietto.", "error");
        }
    };

    // Azione: Upload PDF
    const handleUploadPdf = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Reset input per permettere ricarica stesso file
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }

        // Verifica che sia un PDF
        if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
            showMessage("Seleziona un file PDF valido.", "error");
            return;
        }

        if (file.size > 20 * 1024 * 1024) {
            showMessage("Il file PDF non può superare i 20 MB.", "error");
            return;
        }

        setPendingPdfFile(file);
        setPdfDownloadLink(file.name);
        showMessage("PDF selezionato. Verrà caricato al salvataggio.", "success");
    };

    const uploadPendingPdf = async (externalActivityId?: string | number | null): Promise<string> => {
        if (!pendingPdfFile) return pdfDownloadLink;

        setIsUploadingPdf(true);
        try {
            const url = await uploadTicketPdf(pendingPdfFile, externalActivityId);
            setPendingPdfFile(null);
            return url;
        } finally {
            setIsUploadingPdf(false);
        }
    };

    // Azione: Upload Audio PDF
    const handleUploadAudioPdf = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (audioFileInputRef.current) {
            audioFileInputRef.current.value = "";
        }

        if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
            showMessage("Seleziona un file PDF valido.", "error");
            return;
        }

        if (file.size > 20 * 1024 * 1024) {
            showMessage("Il file PDF non può superare i 20 MB.", "error");
            return;
        }

        setPendingAudioPdfFile(file);
        setAudioGuideDownloadLink(file.name);
        showMessage("PDF audioguida selezionato. Verrà caricato al salvataggio.", "success");
    };

    const uploadPendingAudioPdf = async (externalActivityId?: string | number | null): Promise<string> => {
        if (!pendingAudioPdfFile) return audioGuideDownloadLink;

        setIsUploadingAudioPdf(true);
        try {
            const url = await uploadTicketPdf(pendingAudioPdfFile, externalActivityId, 'audioguide');
            setPendingAudioPdfFile(null);
            return url;
        } finally {
            setIsUploadingAudioPdf(false);
        }
    };

    const handleAddNote = async () => {
        if (!noteInput.trim() || localSavingNote || isSavingNotes) return;
        setLocalSavingNote(true);
        try {
            const success = await upsertBookingNote(
                booking.ExternalBookingId.toString(),
                userId || "-1",
                noteInput
            );
            if (success) {
                await onAddNote(noteInput);
                setNoteInput("");
                showMessage("Nota aggiunta al diario di bordo.", "success");
            } else {
                showMessage("Errore nel salvataggio della nota.", "error");
            }
        } catch (error) {
            console.error("Errore sistemi Modal:", error);
            showMessage("Impossibile connettersi al server per le note.", "error");
        } finally {
            setLocalSavingNote(false);
        }
    };

    const isConfirmed = booking.BookingStatus === "CONFIRMED";
    const normalizedExternalId = String(booking.externalId ?? "").trim().toLowerCase();
    const shouldShowTicketSection = normalizedExternalId === "tk2" || normalizedExternalId === "tk3";
    const ticketSummaryLines = getTicketSummaryLines(booking.TicketsSummary);

    const TicketSummaryColorRenderer = ({ data, value }: { data: any; value: any }) => {
        if (!value) return null;
        const hasTicket = !!data?.TicketNumber;
        const sent = data?.ticketIsSentToCustomer;

        let bgColor = "rgba(255, 166, 0, 1)";
        let textColor = "#682704";

        if (hasTicket && !sent) {
            bgColor = "rgb(230, 255, 9)";
            textColor = "rgb(49, 48, 5)";
        } else if (hasTicket && sent) {
            bgColor = "rgba(46, 125, 50, 0.5)";
            textColor = "#2dd135ff";
        }

        return (
            <Box sx={{
                ml: 1, px: 1.5, py: 0.6, borderRadius: "4px",
                fontSize: "0.75rem", fontWeight: "bold", textTransform: "uppercase",
                display: "inline-block", backgroundColor: bgColor, color: textColor,
            }}>
                {value}
            </Box>
        );
    };

    return (
        <>
            <Modal open={open} onClose={handleCloseModal}>
                <Box sx={{
                    position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
                    bgcolor: "background.paper", color: "text.primary",
                    borderRadius: { xs: 0, sm: 2 }, width: { xs: "100%", md: "85%", lg: "80%" },
                    height: { xs: "100dvh", sm: "auto" }, maxHeight: { xs: "100dvh", sm: "95vh" }, boxShadow: 24,
                    border: theme.palette.mode === 'dark' ? `1px solid ${theme.palette.divider}` : 'none',
                    display: 'flex', flexDirection: 'column', overflow: 'hidden'
                }}>

                    {/* HEADER */}
                    <Box sx={{
                        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1,
                        px: { xs: 2, md: 3 }, py: 1.2,
                        borderBottom: `1px solid ${theme.palette.divider}`
                    }}>
                        <Box sx={{ display: "flex", alignItems: { xs: "flex-start", sm: "center" }, flexDirection: { xs: "column", sm: "row" }, gap: { xs: 0.5, sm: 2 }, minWidth: 0 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                Prenotazione #{booking.BookingId}
                            </Typography>
                            <Box sx={{
                                px: 1.5, py: 0.5, borderRadius: "4px", fontSize: "0.75rem", fontWeight: "bold",
                                textTransform: "uppercase",
                                backgroundColor: isConfirmed ? "rgba(46, 125, 50, 0.5)" : "rgba(241, 89, 89, 0.5)",
                                color: isConfirmed ? "#2dd135ff" : "#940101ff",
                                display: "inline-block"
                            }}>
                                {booking.BookingStatus}
                            </Box>
                        </Box>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* BODY */}
                    <Box sx={{ px: { xs: 1.5, sm: 2, md: 4 }, py: { xs: 1.5, sm: 2 }, overflowY: "auto", flexGrow: 1, minHeight: 0 }}>
                        <Grid container spacing={1}>
                            <Grid item xs={12} md={6}>
                                <Row label="Inizio" value={safeFormatDate(booking.StartDateTime, "dd/MM/yyyy HH:mm")} />
                                <Row label="Codice" value={
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        {booking.LinkBokun ? (
                                            <Link href={booking.LinkBokun} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                                                {booking.ProductConfirmationCode}
                                            </Link>
                                        ) : (
                                            booking.ProductConfirmationCode
                                        )}
                                        <IconButton size="small" onClick={() => copyToClipboard(booking.ProductConfirmationCode ?? "")}>
                                            <ContentCopyIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Box>
                                } />
                                <Row label="Canale" value={booking.ChannelType} />
                                <Row label="External ID TK" value={booking.externalId} />
                                <Row label="OTA" value={booking.OTA} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Row label="Cliente" value={
                                    customerName ? (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            <Typography sx={{ fontSize: '0.9rem' }}>{customerName}</Typography>
                                            <IconButton
                                                size="small"
                                                aria-label="Copia nome cliente"
                                                onClick={() => copyToClipboard(customerName)}
                                            >
                                                <ContentCopyIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </Box>
                                    ) : "-"
                                } />
                                <Row label="Titolo" value={booking.Title} />
                                <Row label="Rate Title" value={booking.rateTitle} />
                                <Row label="Email" value={
                                    booking.Email ? (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            <Link href={`mailto:${booking.Email}`} underline="hover" sx={{ fontSize: '0.9rem' }}>{booking.Email}</Link>
                                            <IconButton size="small" onClick={() => copyToClipboard(booking.Email ?? "")}>
                                                <ContentCopyIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </Box>
                                    ) : "-"
                                } />
                                <Row label="Telefono" value={booking.PhoneNumber} />
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 1.5 }} />

                        {/* BIGLIETTI */}
                        <Row label="Biglietti" labelSx={{ fontSize: "1.1rem", fontWeight: 600 }} value={
                            ticketSummaryLines.length > 0 ? ticketSummaryLines.map((ticket, i) => {
                                return (
                                    <Typography key={i} sx={{ fontSize: "0.85rem", fontWeight: 500, lineHeight: 1.2 }}>
                                        {ticket.label}
                                        {ticket.quantity ? <> x <span style={{ color: theme.palette.error.main, fontWeight: 700 }}>{ticket.quantity}</span></> : null}
                                    </Typography>
                                );
                            }) : "-"
                        } />

                        {/* PASSEGGERI */}
                        {passengers.length > 0 && (
                            <Box sx={{ mt: 1.5, p: 1.5, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa', borderRadius: 1, border: `1px solid ${theme.palette.divider}` }}>
                                <Typography variant="caption" sx={{ mb: 1, display: 'block', color: "primary.main", fontWeight: "bold", textTransform: 'uppercase' }}>
                                    DETTAGLIO PASSEGGERI ({passengers.length})
                                </Typography>
                                <Grid container spacing={1}>
                                    {passengers.map((p: Passenger, idx: number) => {
                                        const age = calculateAge(p.PassengerDateOfBirth);
                                        return (
                                            <Grid item xs={12} sm={6} key={idx}>
                                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 0.3 }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                        {p.LeadPassenger ? <StarIcon sx={{ fontSize: 16, color: "#ffa000" }} /> : <Box sx={{ width: 16 }} />}
                                                        <Typography sx={{ fontSize: "0.85rem" }}>
                                                            {`${p.PassengerFirstName ?? ""} ${p.PassengerLastName ?? ""}`.trim() || "N/D"}
                                                            <Typography component="span" sx={{ fontSize: "0.75rem", color: 'text.secondary' }}> ({p.TicketCategory})</Typography>
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                        {age && <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{age} <CakeIcon sx={{ fontSize: 12, ml: 0.2 }} /></Typography>}
                                                        {p.PassengerNationality && <Typography sx={{ fontSize: '0.7rem', fontWeight: 'bold', border: `1px solid ${theme.palette.divider}`, px: 0.4 }}>{p.PassengerNationality}</Typography>}
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Box>
                        )}

                        {/* SEZIONE CODICE BIGLIETTO */}
                        {shouldShowTicketSection && (
                            <Box sx={{ mt: 2 }}>
                                <Grid container spacing={2} sx={{ mb: 2 }}>

                                    <Grid item xs={12} md={2}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            Lingua
                                        </Typography>
                                        <FormControl fullWidth size="small">
                                            <InputLabel id="ticket-language-label"></InputLabel>
                                            <Select
                                                labelId="ticket-language-label"
                                                label=""
                                                value={ticketLanguage}
                                                displayEmpty
                                                onChange={(e) => setTicketLanguage(e.target.value as TicketLanguageValue)}
                                                renderValue={(selected) => selected ? ticketLanguages[selected as TicketLanguage] : ""}
                                                sx={{ "& .MuiSelect-select": { fontSize: '0.85rem' } }}
                                            >
                                                {ticketLanguageEntries.filter(([value]) => !usesUffiziAudioGuide || !["kr", "cn", "jp"].includes(value)).map(([value, label]) => (
                                                    <MenuItem key={value} value={value}>
                                                        {label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} md={5}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            Link Documento PDF
                                        </Typography>
                                        <TextField
                                            fullWidth size="small"
                                            placeholder="Incolla qui il link al PDF..."
                                            value={pdfDownloadLink} // MODIFICATO QUI: uso dello stato locale invece di pdfLink
                                            onChange={(e) => setPdfDownloadLink(e.target.value)}
                                            sx={{ "& .MuiOutlinedInput-root": { fontSize: '0.85rem' } }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PictureAsPdfIcon sx={{ fontSize: 18, color: '#d32f2f' }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <Button
                                            component="label"
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            startIcon={isUploadingPdf ? <CircularProgress size={16} /> : <UploadFileIcon sx={{ fontSize: 18 }} />}
                                            disabled={isUploadingPdf}
                                            sx={{ mt: 1, height: 36, textTransform: 'none' }}
                                        >
                                            {isUploadingPdf ? "Caricamento..." : "Carica PDF"}
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept="application/pdf,.pdf"
                                                hidden
                                                onChange={handleUploadPdf}
                                            />
                                        </Button>
                                    </Grid>

                                    <Grid item xs={12} md={5}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            Link Audioguida
                                        </Typography>
                                        <TextField
                                            fullWidth size="small"
                                            placeholder="Incolla qui il link all'audioguida..."
                                            value={audioGuideDownloadLink}
                                            onChange={(e) => setAudioGuideDownloadLink(e.target.value)}
                                            helperText={usesUffiziAudioGuide ? "PDF audioguida inserito automaticamente per TK2/TK3" : undefined}
                                            sx={{ "& .MuiOutlinedInput-root": { fontSize: '0.85rem' } }}
                                            InputProps={{
                                                readOnly: usesUffiziAudioGuide,
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PictureAsPdfIcon sx={{ fontSize: 18, color: '#d32f2f' }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        {!usesUffiziAudioGuide && (
                                            <Button
                                                component="label"
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                                startIcon={isUploadingAudioPdf ? <CircularProgress size={16} /> : <UploadFileIcon sx={{ fontSize: 18 }} />}
                                                disabled={isUploadingAudioPdf}
                                                sx={{ mt: 1, height: 36, textTransform: 'none' }}
                                            >
                                                {isUploadingAudioPdf ? "Caricamento..." : "Carica PDF"}
                                                <input
                                                    ref={audioFileInputRef}
                                                    type="file"
                                                    accept="application/pdf,.pdf"
                                                    hidden
                                                    onChange={handleUploadAudioPdf}
                                                />
                                            </Button>
                                        )}
                                    </Grid>
                                </Grid>

                                <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Codice Biglietto
                                    <TicketSummaryColorRenderer data={booking} value={booking.TicketNumber ? (booking.ticketIsSentToCustomer ? "Inviato!" : "Da Inviare..") : " No Ticket! "} />
                                </Typography>

                                <Grid container spacing={1} alignItems="center">
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth size="small"
                                            placeholder="Inserisci il codice..."
                                            value={localTicketNumber}
                                            onChange={(e) => setLocalTicketNumber(e.target.value)}
                                            InputProps={{
                                                endAdornment: localTicketNumber && (
                                                    <InputAdornment position="end">
                                                        <IconButton size="small" onClick={() => copyToClipboard(localTicketNumber)} edge="end">
                                                            <ContentCopyIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                            sx={{ "& .MuiOutlinedInput-root": { fontFamily: 'monospace', fontSize: '0.9rem' } }}
                                        />
                                    </Grid>

                                    <Grid item xs={6} md={3}>
                                        <Button
                                            fullWidth variant="contained" size="small"
                                            onClick={handleSaveTicketOnly}
                                            disabled={!localTicketNumber.trim() || isActionDisabled}
                                            sx={{ height: 40 }}
                                        >
                                            {isActionDisabled && !ticketSent ? <CircularProgress size={18} color="inherit" /> : "Salva codice"}
                                        </Button>
                                    </Grid>
                                            {/*disabled={!localTicketNumber.trim() || !pdfDownloadLink.trim() || isSavingTicket}
                                            startIcon={isSavingTicket && ticketSent ? <CircularProgress size={18} color="inherit" /> : <SendIcon />
                                            disabled={!localTicketNumber.trim() || !pdfLink.trim() || (processStatus?.status === 'processing')}
                                            }
                                            */}
                                    <Grid item xs={6} md={3}>
                                        <Button
                                            fullWidth
                                            variant={ticketSent ? "contained" : "outlined"}
                                            color={ticketSent ? "success" : "primary"}                               
                                            size="small"
                                            onClick={handleSendAndSave}
                                            disabled={!localTicketNumber.trim() || !pdfDownloadLink.trim() || isActionDisabled}
                                            
                                            startIcon={isActionDisabled ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                                            sx={{ height: 40, whiteSpace: 'nowrap' }}
                                        >
                                            {ticketSent ? "Inviato" : "Invia e Salva"}
                                        </Button>
                                    </Grid>
{/* FEEDBACK WEBHOOK
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', my: 2 }}>
                                        {showProcessFeedback && <StatusBadge status={webhookStatus} />}
                                    </Box>
 */}
                                </Grid>
                                {/* FEEDBACK IN TEMPO REALE DAI SOCKET */}
                                {showProcessFeedback && processStatus && (
                                    <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, bgcolor: isProcessError ? 'error.main' : 'primary.main', color: 'black' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {isProcessRunning && <CircularProgress size={16} color="inherit" />}
                                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                                {processStatus.message || "Operazione in corso..."}
                                            </Typography>
                                        </Box>
                                        {isProcessError && (
                                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                                                {processStatus.error || lastError}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                                <Divider sx={{ my: 1.5 }} />
                            </Box>
                        )}

                        {/* SEZIONE NOTE */}
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Cronologia Note</Typography>
                                <Chip label={notes.length} size="small" color="primary" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 'bold' }} />
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, mb: 1.5 }}>
                                <TextField
                                    fullWidth size="small" multiline minRows={1} maxRows={3}
                                    placeholder="Scrivi una nuova nota..."
                                    value={noteInput}
                                    onChange={(e) => setNoteInput(e.target.value)}
                                    sx={{ "& .MuiInputBase-root": { fontSize: '0.85rem' } }}
                                />
                                <Button
                                    variant="contained" size="small"
                                    onClick={handleAddNote}
                                    disabled={!noteInput.trim() || localSavingNote || isSavingNotes}
                                    sx={{ minWidth: { xs: '100%', sm: 40 }, width: { xs: '100%', sm: 40 }, height: 40, p: 0 }}
                                >
                                    {(localSavingNote || isSavingNotes) ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                                </Button>
                            </Box>

                            <Box sx={{
                                maxHeight: 180, overflowY: 'auto',
                                border: `1px solid ${theme.palette.divider}`, borderRadius: 1,
                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.1)' : '#fafafa'
                            }}>
                                {notes.length === 0 ? (
                                    <Typography variant="caption" sx={{ p: 2, display: 'block', textAlign: 'center', color: 'text.secondary' }}>Nessuna nota presente.</Typography>
                                ) : (
                                    <List dense sx={{ py: 0 }}>
                                        {notes.map((n, idx) => (
                                            <React.Fragment key={n.id}>
                                                <ListItem alignItems="flex-start" sx={{ py: 0.8 }}>
                                                    <Avatar sx={{ width: 24, height: 24, mr: 1.5, mt: 0.5, bgcolor: theme.palette.primary.light }}>
                                                        <PersonIcon sx={{ fontSize: 16 }} />
                                                    </Avatar>
                                                    <ListItemText
                                                        primary={
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>{n.firstName + " " + n.lastName}</Typography>
                                                                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                                                    {safeFormatDate(n.createdAt, "dd/MM HH:mm")}
                                                                </Typography>
                                                            </Box>
                                                        }
                                                        secondary={<Typography sx={{ fontSize: '0.85rem', color: 'text.primary', whiteSpace: 'pre-line' }}>{n.message}</Typography>}
                                                    />
                                                </ListItem>
                                                {idx < notes.length - 1 && <Divider component="li" />}
                                            </React.Fragment>
                                        ))}
                                    </List>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    {/* FOOTER */}
                    <Box sx={{ px: { xs: 2, md: 3 }, py: 1.5, display: "flex", justifyContent: "flex-end", borderTop: `1px solid ${theme.palette.divider}`, bgcolor: "background.paper" }}>
                        <Button onClick={handleCloseModal} size="small" color="inherit">Chiudi</Button>
                    </Box>
                </Box>
            </Modal>

            {/* FEEDBACK SNACKBAR */}
            <Snackbar open={snackbar.open} autoHideDuration={syncing ? null : 4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {syncing && <CircularProgress size={16} color="inherit" />}
                        {snackbar.message}
                    </Box>
                </Alert>
            </Snackbar>
        </>
    );
};

// OTTIMIZZAZIONE: React.memo impedisce ricalcoli costosi durante la digitazione nel padre
export default React.memo(BookingDetailModal, (prevProps, nextProps) => {
    return (
        prevProps.open === nextProps.open &&
        prevProps.booking === nextProps.booking &&
        prevProps.ticketNumber === nextProps.ticketNumber &&
        prevProps.pdfLink === nextProps.pdfLink && // <-- IMPORTANTE
        prevProps.audioGuideLink === nextProps.audioGuideLink && // <-- IMPORTANTE
        prevProps.ticketSent === nextProps.ticketSent &&
        prevProps.webhookStatus === nextProps.webhookStatus &&
        prevProps.isSavingTicket === nextProps.isSavingTicket &&
        prevProps.notes === nextProps.notes &&
        prevProps.processStatus?.status === nextProps.processStatus?.status &&
        prevProps.processStatus?.message === nextProps.processStatus?.message &&
        prevProps.processStatus?.error === nextProps.processStatus?.error &&
        prevProps.lastError === nextProps.lastError
    );
});
